import { ShareIcon } from "../../../assets/icons";
import { Button } from "@altimateai/lego";
import { createPortal } from "react-dom";
import { useLineageContext } from "../context/LineageProvider";
import { ReactFlowInstance } from "reactflow";
import { isColumn, isNotColumn, isRefEdge } from "../utils";
import { BetterPopover } from "./BetterPopover";
import { useState } from "react";
import { CustomInput } from "./Form";
import { exportLineage, getColumns, showInfoNotification } from "../api";
import { Details, StaticLineage } from "../types";

const ExportButton = ({ flow }: { flow: ReactFlowInstance }) => {
  const exportContainer = document.getElementById("export-container");
  const {
    state: { selectedColumn, collectColumns },
  } = useLineageContext();
  const [name, setName] = useState("");
  const handleExportLineage = async (close: () => void) => {
    // Exclude ERD overlay ref edges from export — they describe FK
    // relationships, not dbt `ref()` lineage. Receiving systems treat
    // entries in `tableEdges`/`columnEdges` as model dependencies, so
    // including ref edges would falsely imply lineage between FK-pair
    // models. ERD relationships are queryable separately via
    // `getRelationships` if downstream consumers want them.
    const edges = flow.getEdges().filter(e => !isRefEdge(e));
    const tableEdges = edges
      .filter(e => isNotColumn(e))
      .map(e => [e.source, e.target] as [string, string]);
    const columnEdges = edges
      .filter(e => isColumn(e))
      .map(
        e =>
          // remove column prefix for edge
          [e.source.slice(7), e.target.slice(7), e.data?.type] as [string, string, string]
      );
    const details: Details = {};
    await Promise.all(
      flow.getNodes().map(async n => {
        if (isColumn(n)) return;
        const data = await getColumns(n.id, false);
        details[n.id] = {
          type: n.data.nodeType,
          nodeId: n.id,
          name: n.data.label,
          ...data,
        };
      })
    );

    const lineage_data: StaticLineage = {
      selectedColumn: selectedColumn!,
      collectColumns,
      tableEdges,
      columnEdges,
      details,
    };
    const response = await exportLineage({ name, lineage_data });
    if (response?.url) {
      showInfoNotification(`Lineage is exported at [link](${response.url})`);
    }
    close();
    setName("");
  };
  if (!exportContainer) {
    return null;
  }
  return createPortal(
    <BetterPopover
      id="export-popover"
      trigger={
        <Button variant="default" size="xs">
          <ShareIcon />
          Export
        </Button>
      }
      render={({ close }) => {
        return (
          <div>
            <CustomInput
              id="export-name"
              value={name}
              type="text"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              className="mb-2"
              placeholder="Enter name"
            />
            <Button
              variant={name === "" ? "secondary" : "default"}
              size="sm"
              onClick={() => handleExportLineage(close)}
              disabled={name === ""}
            >
              <ShareIcon />
              Export
            </Button>
          </div>
        );
      }}
    />,
    exportContainer
  );
};

export default ExportButton;
